import React, { useEffect, useRef, useState } from 'react';
import { MapPin, X, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

function ClubMap({ clubs, userLocation }) {
  const navigate = useNavigate();
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const [selectedClusters, setSelectedClusters] = useState([]);

  // Fonction pour regrouper les clubs par proximité (environ 5km)
  const clusterClubs = (clubs) => {
    const clusters = [];
    const processed = new Set();
    const radiusKm = 5;

    clubs.forEach((club, index) => {
      if (processed.has(index)) return;
      
      if (!club.location || !club.location.coordinates) return;
      
      const [lng1, lat1] = club.location.coordinates;
      if (!lat1 || !lng1 || isNaN(lat1) || isNaN(lng1)) return;

      const cluster = {
        clubs: [club],
        center: { lat: lat1, lng: lng1 }
      };

      clubs.forEach((otherClub, otherIndex) => {
        if (processed.has(otherIndex) || index === otherIndex) return;
        
        if (!otherClub.location || !otherClub.location.coordinates) return;
        
        const [lng2, lat2] = otherClub.location.coordinates;
        if (!lat2 || !lng2 || isNaN(lat2) || isNaN(lng2)) return;

        const distance = getDistanceFromLatLonInKm(lat1, lng1, lat2, lng2);
        
        if (distance <= radiusKm) {
          cluster.clubs.push(otherClub);
          processed.add(otherIndex);
        }
      });

      processed.add(index);
      clusters.push(cluster);
    });

    return clusters;
  };

  const getDistanceFromLatLonInKm = (lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const dLat = deg2rad(lat2 - lat1);
    const dLon = deg2rad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const d = R * c;
    return d;
  };

  const deg2rad = (deg) => {
    return deg * (Math.PI / 180);
  };

  useEffect(() => {
    let mapInstance = null;

    const loadMap = async () => {
      if (!mapRef.current || mapRef.current._leaflet_id) {
        return;
      }

      const L = await import('leaflet');
      
      delete L.Icon.Default.prototype._getIconUrl;
      L.Icon.Default.mergeOptions({
        iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
        iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
        shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
      });

      const center = userLocation 
        ? [userLocation.latitude, userLocation.longitude]
        : [45.5017, -73.5673];

      // CRITIQUE : Fixer le z-index pour que la carte reste sous la navbar
      mapInstance = L.map(mapRef.current, {
        zoomControl: true
      }).setView(center, 12);

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: 'Â© OpenStreetMap contributors'
      }).addTo(mapInstance);

      if (userLocation) {
        const userIcon = L.divIcon({
          className: 'user-location-marker',
          html: '<div style="background: #4F46E5; width: 16px; height: 16px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>',
          iconSize: [22, 22]
        });

        L.marker([userLocation.latitude, userLocation.longitude], { icon: userIcon })
          .addTo(mapInstance)
          .bindPopup('<b>Votre position</b>');
      }

      const clusters = clusterClubs(clubs);

      clusters.forEach((cluster) => {
        const { lat, lng } = cluster.center;
        const clubCount = cluster.clubs.length;
        
        const clusterIcon = L.divIcon({
          className: 'club-marker',
          html: `<div style="background: linear-gradient(135deg, #EF4444 0%, #DC2626 100%); width: 40px; height: 40px; border-radius: 50%; border: 3px solid white; box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4); display: flex; align-items: center; justify-content: center; color: white; font-weight: bold; font-size: 16px; cursor: pointer; transition: transform 0.2s;">${clubCount}</div>`,
          iconSize: [46, 46]
        });

        const marker = L.marker([lat, lng], { icon: clusterIcon })
          .addTo(mapInstance)
          .on('click', () => {
            setSelectedClusters(cluster.clubs);
            setTimeout(() => {
              document.getElementById('clubs-list')?.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'nearest' 
              });
            }, 100);
          });

        const clubNames = cluster.clubs.map(c => c.nom).join('<br>');
        marker.bindPopup(`
          <div style="min-width: 280px; max-width: 350px; padding: 4px;">
            <h3 style="font-weight: bold; font-size: 17px; margin-bottom: 12px; color: #1F2937; border-bottom: 2px solid #4F46E5; padding-bottom: 8px;">
              ${clubCount} club${clubCount > 1 ? 's' : ''} dans cette zone
            </h3>
            <div style="max-height: 200px; overflow-y: auto; color: #374151; font-size: 15px; line-height: 2; padding-right: 8px;">
              ${clubNames}
            </div>
            <p style="margin-top: 12px; padding-top: 8px; border-top: 1px solid #E5E7EB; font-size: 13px; color: #6B7280; font-weight: 600; display: flex; align-items: center; gap: 4px;">
              <span style="font-size: 16px;">👆</span>Cliquez pour voir les détails</p>
          </div>
        `);
      });
    };

    loadMap();

    return () => {
      if (mapInstance) {
        mapInstance.remove();
        mapInstance = null;
      }
    };
  }, [clubs, userLocation]);

  const handleClubClick = (clubId) => {
    navigate(`/club/${clubId}`);
  };

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden relative">
      {/* Header avec gradient */}
      <div className="p-3 sm:p-4 bg-slate-800">
        <h2 className="text-xl sm:text-2xl font-bold text-white flex items-center">
          <MapPin className="mr-2 sm:mr-3" size={24} />
          Carte Interactive
        </h2>
        <p className="text-indigo-100 mt-1 text-sm sm:text-base">
          Cliquez sur les marqueurs pour explorer
        </p>
      </div>

      {/* Carte - CRITIQUE: style pour fixer le z-index */}
      <div 
        ref={mapRef} 
        className="relative"
        style={{ 
          height: '400px', 
          width: '100%',
          zIndex: 1  // CRITIQUE : z-index bas pour rester sous la navbar
        }}
      ></div>

      {/* Overlay pour les clubs sélectionnés - Mobile optimized */}
      {selectedClusters.length > 0 && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
            onClick={() => setSelectedClusters([])}
          ></div>

          {/* Modal mobile / Section desktop */}
          <div 
            id="clubs-list" 
            className="fixed md:relative bottom-0 left-0 right-0 md:border-t md:border-gray-200 bg-slate-800 z-50 md:z-auto rounded-t-2xl md:rounded-none shadow-2xl md:shadow-none max-h-[70vh] md:max-h-none overflow-y-auto"
          >
            {/* Handle pour mobile */}
            <div className="md:hidden flex justify-center pt-3 pb-2">
              <div className="w-12 h-1.5 bg-gray-300 rounded-full"></div>
            </div>

            <div className="p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2">
                  <MapPin className="text-indigo-600" size={20} />
                  {selectedClusters.length} club{selectedClusters.length > 1 ? 's' : ''} trouvé{selectedClusters.length > 1 ? 's' : ''}
                </h3>
                <button
                  onClick={() => setSelectedClusters([])}
                  className="text-gray-500 hover:text-gray-700 p-2 hover:bg-gray-100 rounded-full transition"
                  aria-label="Fermer"
                >
                  <X size={20} />
                </button>
              </div>
              
              <div className="space-y-3 sm:space-y-4">
                {selectedClusters.map((club) => (
                  <div 
                    key={club._id} 
                    className="bg-gradient-to-br from-slate-600 to-slate-700 rounded-xl p-3 sm:p-4 shadow-sm hover:shadow-md transition-all border border-gray-200"
                  >
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start gap-2 mb-2">
                          <h4 className="text-base sm:text-lg font-bold text-white flex-1">{club.nom}</h4>
                          <span className="bg-indigo-100 text-indigo-800 px-2 py-0.5 rounded-full text-xs font-bold whitespace-nowrap">
                            {club.niveau}
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-2 text-xs sm:text-sm text-white">
                          <div className="flex items-center gap-1.5">
                            <MapPin size={14} className="text-white flex-shrink-0" />
                            <span className="truncate">{club.ville}</span>
                          </div>
                          <div>
                            <span className="font-semibold">Distance:</span> {club.distance}
                          </div>
                          <div className="col-span-2">
                            <span className="font-semibold">Membres:</span> {club.membres?.length || 0}
                          </div>
                        </div>
                        
                        {club.description && (
                          <p className="text-xs sm:text-sm text-gray-400 mt-2 line-clamp-2">
                            {club.description}
                          </p>
                        )}
                      </div>
                      
                      <button
                        onClick={() => handleClubClick(club._id)}
                        className="w-full sm:w-auto sm:ml-4 px-4 py-2 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white rounded-lg hover:from-indigo-700 hover:to-indigo-800 active:scale-95 transition-all font-semibold text-sm whitespace-nowrap shadow-md flex items-center justify-center gap-2"
                      >
                        <Eye size={16} />
                        <span>Voir</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Légende - Améliorée */}
      <div className="p-3 sm:p-4 bg-slate-700 border-t border-slate-600">
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 text-xs sm:text-sm text-white">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-indigo-600 rounded-full shadow-sm"></div>
            <span className="font-medium">Votre position</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-gradient-to-br from-red-500 to-red-600 rounded-full shadow-sm flex items-center justify-center">
              <span className="text-white text-[8px] font-bold"></span>
            </div>
            <span className="font-medium">Clubs dans la zone</span>
          </div>
        </div>
      </div>

      {/* Style global pour fixer le z-index de Leaflet */}
      <style jsx global>{`
        .leaflet-pane {
          z-index: 1 !important;
        }
        .leaflet-top,
        .leaflet-bottom {
          z-index: 2 !important;
        }
        .leaflet-control {
          z-index: 2 !important;
        }
        .leaflet-popup {
          z-index: 3 !important;
        }
        .custom-popup .leaflet-popup-content-wrapper {
          border-radius: 12px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
          padding: 8px;
        }
        .custom-popup .leaflet-popup-content {
          margin: 8px;
          font-family: system-ui, -apple-system, sans-serif;
        }
        .custom-popup .leaflet-popup-tip {
          background: white;
        }
      `}</style>
    </div>
  );
}

export default ClubMap;